import {isFeatureEnabled} from 'feature-flags';
import {Separator} from 'platform/components';
import {Box, Heading, Show, Space, VStack} from 'platform/foundation';

import {featureFlags} from '@dms/feature-flags';
import {i18n} from '@dms/i18n';

import {SourcingType, useSourcingType} from '../../../hooks/useSourcingType';
import {COMPARISON_HEADER_ATTRIBUTE} from '../constants/COMPARISON_HEADER_ATTRIBUTE';
import {COMPARISON_STICKY_POSITION} from '../constants/COMPARISON_STICKY_POSITION';
import {useComparisonHover} from '../hooks/useComparisonHover';
import {ComparisonHoverCell} from './ComparisonHoverCell';

export function LabelColumn() {
  const {hasUnifiedSurfaces} = useComparisonHover();
  const sourcingType = useSourcingType();
  const isClassifieds = sourcingType === SourcingType.CLASSIFIEDS;
  const isAuctions = sourcingType === SourcingType.AUCTIONS;
  const isOriginCountryFilterEnabled = isFeatureEnabled(
    featureFlags.SOURCING_ORIGIN_COUNTRY_FILTER
  );

  return (
    <Box
      width={40}
      paddingLeft={4}
      flexShrink={0}
      backgroundColor="palettes.neutral.10.100"
      position={COMPARISON_STICKY_POSITION}
      left={0}
      borderRight="1px solid"
      borderColor="general.separator"
      zIndex="HEADER"
    >
      {/* Space disables pointer events and Box/Space do not forward data attributes; this native wrapper receives header hovers. */}
      <div {...{[COMPARISON_HEADER_ATTRIBUTE]: true}}>
        <Space vertical={6} />
      </div>
      <Box
        position={COMPARISON_STICKY_POSITION}
        top={0}
        zIndex={hasUnifiedSurfaces ? 'CONTENT' : undefined}
        backgroundColor="palettes.neutral.10.100"
      >
        <div {...{[COMPARISON_HEADER_ATTRIBUTE]: true}}>
          <Space vertical={52} />
          <Heading size={6}>{i18n.t('entity.vehicle.labels.essentials')}</Heading>
          <Space vertical={1} />
          <Separator spacing={0} />
        </div>
      </Box>
      <div {...{[COMPARISON_HEADER_ATTRIBUTE]: true}}>
        <Space vertical={1} />
      </div>
      <VStack spacing={2}>
        <ComparisonHoverCell isInLabelColumn rowKey="position">
          <Heading size={6} alternative>
            {i18n.t('entity.vehicle.labels.position')}
          </Heading>
        </ComparisonHoverCell>
        <Box height={isFeatureEnabled(featureFlags.SOURCING_PRICE_CHANGES) ? 9 : undefined}>
          <ComparisonHoverCell isInLabelColumn rowKey="adPrice">
            <Heading size={6} alternative isSingleLine>
              {i18n.t('entity.vehicle.labels.adPrice')}
            </Heading>
          </ComparisonHoverCell>
        </Box>
        <ComparisonHoverCell isInLabelColumn rowKey="rankEquipment">
          <Heading size={6} alternative isSingleLine>
            {i18n.t('entity.vehicle.labels.rankEquipment')}
          </Heading>
        </ComparisonHoverCell>
        <Heading size={6}>{i18n.t('general.labels.documents')}</Heading>
      </VStack>
      <Separator spacing={1} />
      <VStack spacing={2}>
        <Show when={isAuctions}>
          <ComparisonHoverCell isInLabelColumn rowKey="registration">
            <Heading size={6} alternative>
              {i18n.t('entity.vehicle.labels.registration')}
            </Heading>
          </ComparisonHoverCell>
        </Show>
        <Show when={isClassifieds}>
          <ComparisonHoverCell isInLabelColumn rowKey="STK">
            <Heading size={6} alternative>
              {i18n.t('entity.vehicle.labels.STK')}
            </Heading>
          </ComparisonHoverCell>
        </Show>
        <ComparisonHoverCell isInLabelColumn rowKey="serviceBook">
          <Heading size={6} alternative>
            {i18n.t('entity.vehicle.labels.serviceBook')}
          </Heading>
        </ComparisonHoverCell>
        <ComparisonHoverCell isInLabelColumn rowKey="mileage">
          <Heading size={6} alternative>
            {i18n.t('entity.vehicle.labels.mileage')}
          </Heading>
        </ComparisonHoverCell>
        <ComparisonHoverCell isInLabelColumn rowKey="manufactureYear">
          <Heading size={6} alternative>
            {i18n.t('entity.vehicle.labels.manufactureYear')}
          </Heading>
        </ComparisonHoverCell>
        <Show when={isOriginCountryFilterEnabled}>
          <ComparisonHoverCell isInLabelColumn rowKey="sellerCountry">
            <Heading size={6} alternative>
              {i18n.t('entity.vehicle.labels.sellerCountry')}
            </Heading>
          </ComparisonHoverCell>
        </Show>
        <ComparisonHoverCell isInLabelColumn rowKey="countryOfOrigin">
          <Heading size={6} alternative>
            {i18n.t('entity.vehicle.labels.countryOfOrigin')}
          </Heading>
        </ComparisonHoverCell>
        <Heading size={6}>{i18n.t('entity.vehicle.labels.performance')}</Heading>
      </VStack>
      <Separator spacing={1} />
      <VStack spacing={2}>
        <ComparisonHoverCell isInLabelColumn rowKey="fuelType">
          <Heading size={6} alternative>
            {i18n.t('entity.vehicle.labels.fuelType')}
          </Heading>
        </ComparisonHoverCell>
        <ComparisonHoverCell isInLabelColumn rowKey="power">
          <Heading size={6} alternative>
            {i18n.t('entity.vehicle.labels.power')}
          </Heading>
        </ComparisonHoverCell>
        <ComparisonHoverCell isInLabelColumn rowKey="engineCapacity">
          <Heading size={6} alternative>
            {i18n.t('entity.vehicle.labels.engineCapacity')}
          </Heading>
        </ComparisonHoverCell>
        <ComparisonHoverCell isInLabelColumn rowKey="transmission">
          <Heading size={6} alternative>
            {i18n.t('entity.vehicle.labels.transmission')}
          </Heading>
        </ComparisonHoverCell>
        <ComparisonHoverCell isInLabelColumn rowKey="driveType">
          <Heading size={6} alternative>
            {i18n.t('entity.vehicle.labels.driveType')}
          </Heading>
        </ComparisonHoverCell>
        <Heading size={6} isSingleLine>
          {i18n.t('entity.vehicle.labels.purchaseInfo')}
        </Heading>
      </VStack>
      <Separator spacing={1} />
      <VStack spacing={2}>
        <ComparisonHoverCell isInLabelColumn rowKey="originalPrice">
          <Heading size={6} alternative>
            {i18n.t('entity.vehicle.labels.originalPrice')}
          </Heading>
        </ComparisonHoverCell>
        <ComparisonHoverCell isInLabelColumn rowKey="recommendedPrice">
          <Heading size={6} alternative>
            {i18n.t('entity.vehicle.labels.recommendedPrice')}
          </Heading>
        </ComparisonHoverCell>
        <ComparisonHoverCell isInLabelColumn rowKey="margin">
          <Heading size={6} alternative>
            {i18n.t('entity.vehicle.labels.margin')}
          </Heading>
        </ComparisonHoverCell>
        <Show when={isClassifieds}>
          <ComparisonHoverCell isInLabelColumn rowKey="costsPrice">
            <Heading size={6} alternative>
              {i18n.t('entity.vehicle.labels.costsPrice')}
            </Heading>
          </ComparisonHoverCell>
        </Show>
        <ComparisonHoverCell isInLabelColumn rowKey="LID">
          <Heading size={6} alternative>
            {i18n.t('entity.vehicle.labels.LID')}
          </Heading>
        </ComparisonHoverCell>
        <Show when={isClassifieds}>
          <ComparisonHoverCell isInLabelColumn rowKey="DOD">
            <Heading size={6} alternative>
              {i18n.t('entity.vehicle.labels.DOD')}
            </Heading>
          </ComparisonHoverCell>
        </Show>
        <Heading size={6}>{i18n.t('entity.vehicle.labels.carLook')}</Heading>
      </VStack>
      <Separator spacing={1} />
      <VStack spacing={2}>
        <ComparisonHoverCell isInLabelColumn rowKey="bodyColor">
          <Heading size={6} alternative>
            {i18n.t('entity.vehicle.labels.bodyColor')}
          </Heading>
        </ComparisonHoverCell>
        <ComparisonHoverCell isInLabelColumn rowKey="interiorMaterial">
          <Heading size={6} alternative>
            {i18n.t('entity.vehicle.labels.interiorMaterial')}
          </Heading>
        </ComparisonHoverCell>
        <Show when={sourcingType === SourcingType.CLASSIFIEDS}>
          <ComparisonHoverCell isInLabelColumn rowKey="numberOfSeats">
            <Heading size={6} alternative>
              {i18n.t('entity.vehicle.labels.numberOfSeats')}
            </Heading>
          </ComparisonHoverCell>
        </Show>
        <ComparisonHoverCell isInLabelColumn rowKey="carStyle">
          <Heading size={6} alternative>
            {i18n.t('entity.vehicle.labels.carStyle')}
          </Heading>
        </ComparisonHoverCell>
        <ComparisonHoverCell isInLabelColumn rowKey="numberOfDoors">
          <Heading size={6} alternative>
            {i18n.t('entity.vehicle.labels.numberOfDoors')}
          </Heading>
        </ComparisonHoverCell>
        <Heading size={6}>{i18n.t('entity.vehicle.labels.features')}</Heading>
      </VStack>
      <Separator spacing={1} />
      <Heading size={6} alternative>
        {i18n.t('entity.vehicle.labels.highlighted')}
      </Heading>
      <Space vertical={44} />
      <Heading size={6} alternative>
        {i18n.t('general.labels.allFeatures')}
      </Heading>
    </Box>
  );
}
